import debounce from 'debounce-promise';

import awsExports from '../../../aws-exports';
import { getClient as getMeiliSearchClient } from '../../../search/meilisearch';

const OFFER_HISTORY_INDEX = `offer-history-${awsExports.stackeryEnvironmentName}`;

const indexNames = {
  offers: OFFER_HISTORY_INDEX,
  users: `users-${awsExports.stackeryEnvironmentName}`,
  policy: `policysummary-${awsExports.stackeryEnvironmentName}`,
  branchPlusPolicies: `branch-plus-policies-${awsExports.stackeryEnvironmentName}`,
  staffUsers: `staff-users-${awsExports.stackeryEnvironmentName}`
};

/**
 * This function mantains a cache of debounced function based on the provided key, in the case of meilisearch search,the searchIndex
 *
 * The point of this is to debounce only similar request.
 * This was done in response to a bug that Dave reported which was as a result of totally
 * different request getting debounced and essentially returning the same data.
 * For example, parallel request to query offer and customer only triggers a single network request
 *
 * @param {*} debuncedFunc
 * @param {*} timeOut
 */
const debounceWithArgs = (debuncedFunc, timeOut) => {
  const debouncedFuncs = {};

  return (key, ...args) => {
    if (!(key in debouncedFuncs)) {
      debouncedFuncs[key] = debounce(debuncedFunc, timeOut);
    }

    return debouncedFuncs[key](key, ...args);
  };
};

const normalizePhoneNumber = (searchString) => {
  if (searchString.match(/\+*1*\(*(\d{3})\)*-*\s*\(*(\d{3})\)*-*\s*\(*(\d{4})\)*/)) {
    return searchString.replace(/\+*1*\(*(\d{3})\)*-*\s*\(*(\d{3})\)*-*\s*\(*(\d{4})\)*/, '$1$2$3');
  }
  return searchString;
};

const mapToAlgoliaResponse = (response) => {
  if (response) {
    return {
      nbHits: response.totalHits || response.estimatedTotalHits || response.hits?.length || 0,
      hits: response.hits || []
    };
  }
  return { nbHits: 0, hits: [] };
};

export const debouncedMeiliSearch = debounceWithArgs(async (indexName, searchParams, userPreferences) => {
  const index = getMeiliSearchClient(userPreferences).index(indexName);
  const { query, attributeForDistinct, limit, offset, ...searchParamsRest } = searchParams;

  if (index) {
    return index
      .search(query, {
        limit,
        offset,
        ...searchParamsRest
      })
      .then((response) => mapToAlgoliaResponse(response));
  }

  return [];
}, 500);

export const searchForWebUserId = async (webUserId, userPreferences) => {
  const indexName = OFFER_HISTORY_INDEX;

  const searchParameters = {
    limit: 250,
    offset: 0,
    attributesToSearchOn: ['webUserId'],
    matchingStrategy: 'all'
  };

  const index = getMeiliSearchClient(userPreferences).index(indexName);
  if (index) {
    try {
      const res = await index.search(webUserId, searchParameters).then((response) => mapToAlgoliaResponse(response));
      return res;
    } catch {
      return [];
    }
  }
  return [];
};

/**
 * Searches for the most recent staff-edited offer for a given webUserId
 * Staff-edited offers have a non-null parentId
 * Only returns offers modified within the past 60 days
 * Excludes the current offer from results
 * Returns additional fields for UI display and analytics
 */
export const searchForNewerStaffEditedOffer = async (webUserId, currentOfferId, userPreferences) => {
  const indexName = OFFER_HISTORY_INDEX;

  // Calculate 60 days ago timestamp for client-side filtering
  const sixtyDaysAgo = new Date();
  sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);
  const sixtyDaysAgoTime = sixtyDaysAgo.getTime();

  const searchParameters = {
    limit: 100,
    offset: 0,
    filter: `webUserId = "${webUserId}" AND parentId EXISTS`
  };

  console.log(`checking for newer offer: ${webUserId}, ${currentOfferId}, ${JSON.stringify(searchParameters)}`);

  const index = getMeiliSearchClient(userPreferences).index(indexName);
  if (index) {
    try {
      const res = await index.search('', searchParameters);
      // Filter for offers within the last 60 days, exclude current offer, and filter out invalid offers
      const newerOffers = res.hits
        .filter((offer) => {
          if (offer.id === currentOfferId) {
            return false;
          }
          if (!offer.updatedDateTime) {
            return false;
          }
          const offerTime = new Date(offer.updatedDateTime).getTime();
          if (offerTime <= sixtyDaysAgoTime) {
            return false;
          }
          // Filter out offers without valid options (failed/ineligible offers)
          const hasValidOptions = Array.isArray(offer.options) && offer.options.length > 0;
          return hasValidOptions;
        })
        .sort((a, b) => {
          // Sort by most recent first
          return new Date(b.updatedDateTime).getTime() - new Date(a.updatedDateTime).getTime();
        });

      if (newerOffers.length === 0) {
        return null;
      }

      const newerOffer = newerOffers[0];

      // Return enhanced data structure with additional fields for UI and analytics
      return {
        id: newerOffer.id,
        webUserId: newerOffer.webUserId,
        updatedDateTime: newerOffer.updatedDateTime,
        parentId: newerOffer.parentId,
        updatedBy: newerOffer.quote?.rep || null,
        policyType: newerOffer.options[0]?.type || null,
        applicantName: `${newerOffer.quote?.fname || ''} ${newerOffer.quote?.lname || ''}`.trim() || null
      };
    } catch (error) {
      console.warn('Error searching for newer staff-edited offer:', error);
      return null;
    }
  }
  return null;
};

const meiliSearch = async (
  { searchString, index, limit = 10, offset = 0, expandedSearch, showOfferHistory },
  userPreferences
) => {
  const isOfferSearch = index === 'offers';
  const indexName = indexNames[index];

  let query = searchString;
  if (index === 'users') {
    // if someone searches by phone number, it's best to search just by the numbers:
    query = normalizePhoneNumber(searchString);
  }

  const searchParameters = {
    query,
    limit,
    offset,
    matchingStrategy: 'all'
  };

  if (index === 'users') {
    searchParameters.attributesToSearchOn = [
      'attributes.name',
      'attributes.family_name',
      'Username',
      'attributes.address',
      'attributes.custom_state',
      'attributes.custom_city',
      'attributes.custom_zip'
    ];

    if (expandedSearch) {
      searchParameters.attributesToSearchOn = searchParameters.attributesToSearchOn.concat([
        'attributes.phone_number',
        'attributes.custom_addl_phone_numbers',
        'attributes.email',
        'attributes.custom_affinity_code'
      ]);
    }
  }

  if (isOfferSearch) {
    searchParameters.attributesToSearchOn = [
      'quote.fname',
      'quote.lname',
      'quote.drivers.firstName',
      'quote.drivers.lastName',
      'quote.submittedAddress.address',
      'quote.submittedAddress.city',
      'quote.submittedAddress.state',
      'quote.submittedAddress.zip',
      'quote.rep',
      'webUserId'
    ];

    if (expandedSearch) {
      searchParameters.attributesToSearchOn = searchParameters.attributesToSearchOn.concat([
        'quote.email',
        'quote.phone',
        'quote.global.affinity'
      ]);
    }
  }

  if (index === 'policy') {
    searchParameters.attributesToHighlight = ['*'];
  }

  if (index === 'branchPlusPolicies') {
    searchParameters.attributesToSearchOn = ['address', 'state', 'customer_first_name', 'customer_last_name'];

    if (expandedSearch) {
      searchParameters.attributesToSearchOn = searchParameters.attributesToSearchOn.concat([
        'phone_number',
        'email',
        'policy_id',
        'account_id'
      ]);
    }
  }

  if (isOfferSearch && !showOfferHistory) {
    searchParameters.distinct = 'webUserId';
  }

  return debouncedMeiliSearch(indexName, searchParameters, userPreferences);
};

export const runSearch = async (
  { searchString, index, limit = 10, offset = 0, expandedSearch, showOfferHistory },
  userPreferences
) => {
  return meiliSearch({ searchString, index, limit, offset, expandedSearch, showOfferHistory }, userPreferences);
};
